<template>
  <div class="card">
    <div class="flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
      <div>
        <h3 class="m-0">Metadata del sitio</h3>
        <small class="text-500">
          Preview de la portada y de cualquier enlace que no sea de una congregación.
        </small>
      </div>
      <div class="flex gap-2">
        <Button
          icon="pi pi-upload"
          label="Subir imagen"
          size="small"
          severity="secondary"
          outlined
          :loading="uploading"
          @click="fileInput?.click()"
        />
        <Button icon="pi pi-check" label="Guardar" size="small" :loading="saving" @click="save" />
      </div>
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png,image/webp,image/gif"
      class="site-file-input"
      @change="onFilePicked"
    />

    <div v-if="loading" class="text-500 text-sm">Cargando…</div>

    <div v-else class="site-grid">
      <!-- Textos -->
      <div class="flex flex-column gap-3">
        <div class="flex flex-column gap-1">
          <label class="text-sm font-medium">Título</label>
          <InputText v-model="draft.title" :placeholder="`Por defecto: ${defaults.title}`" />
        </div>
        <div class="flex flex-column gap-1">
          <label class="text-sm font-medium">Descripción</label>
          <Textarea
            v-model="draft.description"
            rows="3"
            autoResize
            :placeholder="`Por defecto: ${defaults.description}`"
          />
        </div>
        <div class="flex flex-column gap-1">
          <label class="text-sm font-medium">Nombre del sitio</label>
          <InputText v-model="draft.siteName" :placeholder="`Por defecto: ${defaults.siteName}`" />
        </div>
        <div class="flex flex-column gap-1">
          <label class="text-sm font-medium">Caché del preview (segundos)</label>
          <InputNumber
            v-model="draft.previewCacheSeconds"
            :min="0"
            :max="86400"
            showButtons
            buttonLayout="horizontal"
            :step="60"
            inputClass="w-6rem"
          />
          <small class="text-500">
            Cuánto guardan los buscadores y WhatsApp el preview antes de volver a pedirlo.
            Bajalo mientras ajustás la metadata; subilo cuando ya esté estable
            (por defecto {{ settingDefaults.previewCacheSeconds }}).
          </small>
        </div>
        <div>
          <Button
            label="Restablecer textos"
            icon="pi pi-undo"
            size="small"
            severity="secondary"
            text
            :loading="resetting"
            @click="confirmReset"
          />
        </div>
      </div>

      <!-- Vista previa -->
      <div>
        <div class="text-sm font-medium mb-2">Vista previa</div>
        <div class="preview-card">
          <div v-if="previewImageUrl" class="preview-image">
            <img :src="previewImageUrl" alt="Imagen del preview del sitio" />
          </div>
          <div class="preview-body">
            <div class="preview-site">{{ draft.siteName || defaults.siteName }}</div>
            <div class="preview-title">{{ draft.title || defaults.title }}</div>
            <div class="preview-description">{{ draft.description || defaults.description }}</div>
          </div>
        </div>
      </div>

      <!-- Imágenes -->
      <div class="site-images">
        <div class="text-sm font-medium mb-2">
          Imágenes del sitio
          <small class="text-500 font-normal">· recomendado 1200×630, hasta 5MB</small>
        </div>

        <div v-if="!images.length" class="text-500 text-sm">
          Sin imágenes propias: se usa la imagen por defecto de la app.
        </div>

        <div v-else class="image-grid">
          <div
            v-for="img in images"
            :key="img.id"
            class="image-card"
            :class="{ selected: draft.imageId === img.id }"
          >
            <img :src="imageUrl(img.url)" :alt="img.originalName" />
            <div class="image-info">
              <div class="image-name" :title="img.originalName">{{ img.originalName }}</div>
              <div class="text-xs text-500">
                {{ img.width && img.height ? `${img.width}×${img.height} · ` : '' }}{{ formatSize(img.size) }}
              </div>
              <Tag
                v-if="savedImageId === img.id"
                value="Activa"
                severity="success"
                class="text-xs mt-1"
              />
              <div v-else class="text-xs text-400 mt-1">Sin usar</div>
            </div>
            <div class="image-actions">
              <Button
                v-if="draft.imageId === img.id"
                label="Quitar"
                icon="pi pi-times"
                size="small"
                severity="secondary"
                text
                @click="draft.imageId = null"
              />
              <Button v-else label="Usar" icon="pi pi-check" size="small" text @click="draft.imageId = img.id" />
              <Button
                icon="pi pi-trash"
                size="small"
                severity="danger"
                text
                v-tooltip="'Eliminar imagen'"
                @click="confirmDeleteImage(img)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useConfirm } from 'primevue/useconfirm'
import { useAppToast } from '@/composables/useAppToast'
import { useSiteMetadata } from '@/composables/useSiteMetadata'
import { metadataImageUrl } from '@/composables/useLinkMetadata'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import InputNumber from 'primevue/inputnumber'
import Textarea from 'primevue/textarea'
import Tag from 'primevue/tag'

const confirm = useConfirm()
const toast = useAppToast()
const siteApi = useSiteMetadata()

const loading = ref(true)
const saving = ref(false)
const resetting = ref(false)
const uploading = ref(false)
const fileInput = ref(null)

const defaults = ref({ title: '', description: '', siteName: '' })
const settingDefaults = ref({ previewCacheSeconds: 300 })
const images = ref([])
// Lo que está publicado ahora mismo, para distinguirlo del borrador sin guardar.
const savedImageId = ref(null)

const draft = reactive({
  title: '',
  description: '',
  siteName: '',
  imageId: null,
  previewCacheSeconds: 300,
})

const imageUrl = (url) => metadataImageUrl(url)

const previewImage = computed(() => images.value.find((i) => i.id === draft.imageId) || null)

// Sin imagen propia, el enlace se comparte sin og:image (ver publicMeta.js).
const previewImageUrl = computed(() => (previewImage.value ? imageUrl(previewImage.value.url) : null))

const formatSize = (bytes) =>
  bytes > 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`

const load = async () => {
  const res = await siteApi.getSiteMetadata()
  if (res?.error) {
    toast.error('Ocurrió un error', res.error)
    return
  }
  defaults.value = res.defaults
  settingDefaults.value = res.settingDefaults
  images.value = res.images || []
  savedImageId.value = res.metadata.imageId
  draft.title = res.metadata.title
  draft.description = res.metadata.description
  draft.siteName = res.metadata.siteName
  draft.imageId = res.metadata.imageId
  draft.previewCacheSeconds = res.settings.previewCacheSeconds
}

onMounted(async () => {
  await load()
  loading.value = false
})

const save = async () => {
  saving.value = true
  try {
    const res = await siteApi.saveSiteMetadata({ ...draft })
    if (res?.error) {
      toast.error('Ocurrió un error', res.error)
      return
    }
    await load()
    toast.success('Metadata del sitio guardada')
  } finally {
    saving.value = false
  }
}

const confirmReset = () => {
  confirm.require({
    message: '¿Volver a los textos e imagen por defecto del sitio?',
    header: 'Restablecer metadata del sitio',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Restablecer',
    rejectLabel: 'Cancelar',
    accept: async () => {
      resetting.value = true
      try {
        const res = await siteApi.resetSiteMetadata()
        if (res?.error) {
          toast.error('Ocurrió un error', res.error)
          return
        }
        await load()
      } finally {
        resetting.value = false
      }
    },
  })
}

const onFilePicked = async (event) => {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) return
  uploading.value = true
  try {
    const res = await siteApi.uploadImage(file)
    if (res?.error) {
      toast.error('No se pudo subir', res.error)
      return
    }
    images.value = [res.image, ...images.value]
    draft.imageId = res.image.id
    toast.success('Imagen subida', 'Guardá para que la use la portada.')
  } finally {
    uploading.value = false
  }
}

const confirmDeleteImage = (img) => {
  confirm.require({
    message:
      savedImageId.value === img.id
        ? 'La imagen está activa en la portada. Si la eliminás, vuelve la imagen por defecto.'
        : '¿Eliminar esta imagen?',
    header: 'Eliminar imagen',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    acceptLabel: 'Eliminar',
    rejectLabel: 'Cancelar',
    accept: async () => {
      const res = await siteApi.deleteImage(img.id)
      if (res?.error) {
        toast.error('Ocurrió un error', res.error)
        return
      }
      if (draft.imageId === img.id) draft.imageId = null
      await load()
    },
  })
}
</script>

<style scoped>
.site-file-input {
  display: none;
}
.site-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 320px);
  gap: var(--space-4, 1rem);
  align-items: start;
  text-align: left;
}
.site-images {
  grid-column: 1 / -1;
}

.preview-card {
  border: 1px solid var(--surface-border);
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
}
.preview-image {
  aspect-ratio: 1200 / 630;
  background: var(--surface-100);
}
.preview-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.preview-body {
  padding: 0.75rem;
}
.preview-site {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--label-secondary, #6b7280);
}
.preview-title {
  font-weight: 600;
  margin-top: 2px;
}
.preview-description {
  font-size: 0.85rem;
  color: var(--label-secondary, #6b7280);
  margin-top: 2px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
}
.image-card {
  border: 1px solid var(--surface-border);
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.image-card.selected {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 1px var(--primary-color);
}
.image-card img {
  width: 100%;
  aspect-ratio: 1200 / 630;
  object-fit: cover;
  display: block;
  background: var(--surface-100);
}
.image-info {
  padding: 0.5rem 0.75rem;
  flex: 1;
}
.image-name {
  font-size: 0.85rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.image-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 0.5rem 0.5rem;
}

@media (max-width: 900px) {
  .site-grid {
    grid-template-columns: 1fr;
  }
}
</style>
